// 剣舞をアルレッキーノ (Lies of P) の戦い方に落とし込んだ一戦 (2026-10-25 開発版で作り、10-29 配布版の既定へ・?bladeArle=0 で前の一戦)。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md。ここでは技を全部入れ、技と技を「連」でつなぐ。
//   入る技: 旋回突き5連 (β・③の突きに引き戻しの一撃を足した)・遅れの一撃 (γ)・引き寄せ (C)・跳んで叩く (δ)・回って終わる連 (D)・暗器投げ に、
//   終舞の12発から切り出した2つ = 速い連 (B・4発) と 大振り (α・3発で終わりに大きく振る) を足す。
//   連の2拍目からは立ち姿へ戻らず、前の技の振り抜きから次の技の合図の構えへ直接移る (StringBeat.telegraphFromProgress)。
//   連は長いほど弾いた時の体幹の溜まりを下げる (12発の終舞と同じ考え・勝手に崩れない)。
import { ATTACKS, getAttack } from './attacks'
import { BOSS_DEFINITIONS } from './bosses'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import type { AttackDef, BossString, SpriteKey, StringBeat } from './types'
import { BLADE_DANCER_MOVE_STATE_KEYS, TENSE, withDissolves, type BladeTrailStyle, type SoftKey } from './blade_dancer_art'
import { useBladeDancerPirouetteArle, restoreBladeDancerPirouette, type BladePullFx } from './blade_dancer_pirouette'
export { parseBladePullFx } from './blade_dancer_pirouette'
import { BLADE_GAMMA_ID, useBladeDancerGamma, restoreBladeDancerGamma } from './blade_dancer_gamma'
import { BLADE_PULL_ID, useBladeDancerPull, restoreBladeDancerPull } from './blade_dancer_pull'
import { BLADE_DELTA_ID, useBladeDancerDelta, restoreBladeDancerDelta } from './blade_dancer_delta'
import { BLADE_SPIN_ID, useBladeDancerSpin, restoreBladeDancerSpin } from './blade_dancer_spin'
import { BLADE_THROW_ID, useBladeDancerThrow, restoreBladeDancerThrow } from './blade_dancer_throw'
import { CLAP_KEYS, useBladeDancerWaltz, restoreBladeDancerWaltz, waltzStrings } from './blade_dancer_waltz'
import { setBladeClapEnabled } from './blade_dancer_clap'
import { setSpriteFileOverride } from './sprites'

const BOSS_ID = 'blade-dancer'
export const BLADE_QUICK_ID = 'bladeQuick'
export const BLADE_GRAND_ID = 'bladeGrand'
const V = (k: string) => `m1020_v6_${k}`
const W7 = (k: string) => `m1022_w7_${k}`
const SETTLE = 'm1020_waltz_s2'
const STAND = 'm1020_waltz_c6'

// 終舞の拍 (blade_dancer_art.ts の Y2TZ_CRES_*) と同じ: 連の中の一拍 300・大振りの前 320 から当たりと1コマを引く。
const TICK = 16
const CHAIN_GAP = 300 - 120 - TICK
const BIG_GAP = 120 + 200 - TICK

// 発ごとの絵 (当たる絵・その前の絵・振り抜いた絵)。終舞と同じ。
const STRIKE = {
  d2: { pre: 'd1', ft: 'd3' }, d4: { pre: 'd3', ft: 'e1' }, e3: { pre: 'e2', ft: 'e4' }, f2: { pre: 'f1', ft: 'f3' }, g1: { pre: 'f4', ft: 'g2' },
} as const
type Strike = keyof typeof STRIKE
// 終舞の12発の中で、その絵が最初に出る発 (当たりの前端をそこから借りる)。
const CRES_INDEX: Record<Strike, number> = { d2: 0, d4: 1, e3: 2, f2: 3, g1: 11 }

function strikeKeys(strikes: Strike[], gaps: number[]): SoftKey[] {
  return strikes.flatMap((s, i): SoftKey[] => {
    const hit: SoftKey = { phase: 'active', at: 0, image: V(s), hold: true, tag: 'contact', hitIndex: i }
    if (i === strikes.length - 1) return [hit]
    const now = STRIKE[s], next = STRIKE[strikes[i + 1]!]
    const ft: SoftKey = { phase: 'comboGap', at: 0, image: V(now.ft), hold: true, tag: 'followThrough', hitIndex: i }
    if (now.ft === next.pre) return [hit, ft]
    return [hit, ft, { phase: 'comboGap', at: gaps[i] === BIG_GAP ? 0.4 : 0.5, image: V(next.pre), hold: true, hitIndex: i }]
  })
}

// 速い連 (B): 頭の上で剣を交差させた構え (円舞の戻りと同じ絵) から、終舞の頭の4発を一息に。
export const BLADE_QUICK_STRIKES: Strike[] = ['d2', 'd4', 'e3', 'f2']
export const BLADE_QUICK_GAPS_MS = [CHAIN_GAP, CHAIN_GAP, CHAIN_GAP]
const QUICK = { chargeMs: 700, recoveryMs: 700 }
export const BLADE_QUICK_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.1, image: W7('c4'), hold: true, ease: 'easeIn' },            // 頭の上で X 字 (合図)
  { phase: 'telegraph', at: 0.55, image: V('d1'), hold: true, ease: 'easeIn' },
  { phase: 'telegraph', at: 0.85, image: V('d1'), hold: true, ...TENSE },
  ...strikeKeys(BLADE_QUICK_STRIKES, BLADE_QUICK_GAPS_MS),
  // 10-28 締めは五連と同じく段を踏んで立ち姿へ (振り抜きから一足に飛ばない)。
  { phase: 'recovery', at: 0, image: V('f3'), hold: true, tag: 'followThrough', offset: { x: 8 }, rot: 3, ease: 'easeOut' },
  { phase: 'recovery', at: 0.3, image: 'm1020_waltz_s1', hold: true, tag: 'recover', ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.48, image: SETTLE, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.66, image: STAND, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.86, image: 'idle', hold: true },
], { telegraph: QUICK.chargeMs, recovery: QUICK.recoveryMs })

// 大振り (α): 剣を V 字に掲げてから2発刻み、一拍ためて大きく振り下ろす。終わりに肩で息をする (大きな隙)。
export const BLADE_GRAND_STRIKES: Strike[] = ['d4', 'e3', 'g1']
export const BLADE_GRAND_GAPS_MS = [CHAIN_GAP, BIG_GAP]
const GRAND = { chargeMs: 800, recoveryMs: 830 }
export const BLADE_GRAND_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.1, image: V('c4'), hold: true, ease: 'easeIn' },             // V 字に掲げる (合図)
  { phase: 'telegraph', at: 0.6, image: V('d3'), hold: true, ease: 'easeIn' },
  { phase: 'telegraph', at: 0.85, image: V('d3'), hold: true, ...TENSE },
  ...strikeKeys(BLADE_GRAND_STRIKES, BLADE_GRAND_GAPS_MS),
  { phase: 'recovery', at: 0, image: V('g2'), hold: true, tag: 'followThrough', soft: true },
  { phase: 'recovery', at: 0.25, image: V('g3'), hold: true, tag: 'recover', soft: true },  // 肩で息
  { phase: 'recovery', at: 0.75, image: STAND, hold: true, soft: true },
  { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
], { telegraph: GRAND.chargeMs, recovery: GRAND.recoveryMs })

/** 終舞を写して、切り出した発だけの技にする。 */
function cutFromCrescendo(id: string, label: string, strikes: Strike[], gaps: number[], t: { chargeMs: number; recoveryMs: number }, postureDamage: number): AttackDef {
  const c = ATTACKS.crescendo!
  const stages = strikes.map(s => ({ ...c.comboStages![CRES_INDEX[s]]! }))
  const def: AttackDef = {
    ...c, id, label, ...t, postureDamage,
    comboHits: strikes.length, comboGapMs: gaps[0]!, comboGapsMs: [...gaps],
    comboStages: stages,
    ...(stages[0]?.strike ? { strike: { ...stages[0].strike } } : {}),
    ...(c.comboLunge ? { comboLunge: { ...c.comboLunge, followHits: strikes.length, plantFromHit: strikes.length } } : {}),
  }
  delete def.parryPostureMul
  return def
}

// 連の2拍目からの溜めの始まり (その技の溜めの絵で、合図の構えに着いた所)。
export const ARLE_TELEGRAPH_FROM: Record<string, number> = {
  waltzFlurry: 0.38, pirouette: 0.15, [BLADE_GAMMA_ID]: 0.1, [BLADE_PULL_ID]: 0.08, [BLADE_DELTA_ID]: 0.06,
  [BLADE_SPIN_ID]: 0.12, [BLADE_THROW_ID]: 0.12, [BLADE_QUICK_ID]: 0.1, [BLADE_GRAND_ID]: 0.1,
}
const LINK_GAP_MS = 90   // 前の技の振り抜きで止まる間

function link(attackId: string): StringBeat {
  const from = ARLE_TELEGRAPH_FROM[attackId] ?? 0
  return { attackId, gapMs: LINK_GAP_MS, telegraphFromProgress: from, chargeOverrideMs: Math.round(getAttack(attackId).chargeMs * (1 - from)) }
}

/** 連の発の合計から、弾いた時の体幹の溜まりの倍率を決める (4発まで 1・5〜7発 0.75・8発から 0.55)。 */
export function arleParryPostureMul(totalHits: number): number {
  return totalHits <= 4 ? 1 : totalHits <= 7 ? 0.75 : 0.55
}

type Plan = { id: string; ids: string[]; unlock: number; preferFar?: boolean; preferMid?: boolean }
const A = 'waltzFlurry', BETA = 'pirouette', HEM = 'hemSweep', CRES = 'crescendo'
export const ARLE_STRING_PLANS: Plan[] = [
  { id: 'arle-a', ids: [A], unlock: 0 },
  { id: 'arle-a-beta', ids: [A, BETA], unlock: 0 },
  { id: 'arle-a-gamma', ids: [A, BLADE_GAMMA_ID], unlock: 0 },
  { id: 'arle-a-pull-grand', ids: [A, BLADE_PULL_ID, BLADE_GRAND_ID], unlock: 0 },
  { id: 'arle-pull-grand', ids: [BLADE_PULL_ID, BLADE_GRAND_ID], unlock: 0, preferMid: true },
  { id: 'arle-beta', ids: [BETA], unlock: 0 },
  { id: 'arle-throw', ids: [BLADE_THROW_ID], unlock: 0, preferFar: true },
  { id: 'arle-throw-pull-grand', ids: [BLADE_THROW_ID, BLADE_PULL_ID, BLADE_GRAND_ID], unlock: 1, preferFar: true },
  { id: 'arle-a-quick-grand', ids: [A, BLADE_QUICK_ID, BLADE_GRAND_ID], unlock: 1 },
  { id: 'arle-quick-spin-delta', ids: [BLADE_QUICK_ID, BLADE_SPIN_ID, BLADE_DELTA_ID], unlock: 1 },
  { id: 'arle-spin-delta', ids: [BLADE_SPIN_ID, BLADE_DELTA_ID], unlock: 1 },
  { id: 'arle-hem', ids: [HEM], unlock: 1 },
  { id: 'arle-pull-quick-grand', ids: [BLADE_PULL_ID, BLADE_QUICK_ID, BLADE_GRAND_ID], unlock: 1 },
  { id: 'arle-quick-a-gamma', ids: [BLADE_QUICK_ID, A, BLADE_GAMMA_ID], unlock: 1 },
  { id: 'arle-gamma', ids: [BLADE_GAMMA_ID], unlock: 1 },
  { id: 'arle-crescendo', ids: [CRES], unlock: 2 },
  { id: 'arle-a-quick-spin-delta', ids: [A, BLADE_QUICK_ID, BLADE_SPIN_ID, BLADE_DELTA_ID], unlock: 2 },
]

export function buildArleStrings(): BossString[] {
  return ARLE_STRING_PLANS.map(p => {
    const hits = p.ids.reduce((n, id) => n + (getAttack(id).comboHits ?? 1), 0)
    const mul = p.ids.length === 1 ? 1 : arleParryPostureMul(hits)   // 1技だけの連は技の倍率のまま (終舞は 0.5 を持っている)
    return {
      id: p.id, weight: 1, unlockAtExecutes: p.unlock,
      beats: p.ids.map((id, i) => i === 0 ? { attackId: id } : link(id)),
      ...(mul !== 1 ? { parryPostureMul: mul } : {}),
      ...(p.preferFar ? { preferFar: true } : {}),
      ...(p.preferMid ? { preferMid: true } : {}),
    }
  })
}

let installed = false
let baseSnap: string[] = []
let unlockSnap: { atExecutes: number; ids: string[] }[] | undefined

/** 剣舞をアルレッキーノの一戦にする (DEV ?bladeArle=1・useBladeDancerTempoY2tz の後に呼ぶ)。 */
//   pullFx = 旋回突きの引き戻しの跡の案 (?bladePullFx=a|b|c|old・既定 c・null = 10-27 の跡)。
export function useBladeDancerArle(trail: BladeTrailStyle = 'afterimage', pullFx: BladePullFx | null = null): void {
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  if (!installed) {
    baseSnap = [...def.baseAttackIds]
    unlockSnap = def.unlockByExecutes?.map(u => ({ ...u, ids: [...u.ids] }))
  }
  useBladeDancerPirouetteArle(trail, pullFx)
  useBladeDancerGamma(trail)
  useBladeDancerPull(trail)
  useBladeDancerDelta(trail)
  useBladeDancerSpin(trail)
  useBladeDancerThrow()
  if (!installed) {
    ATTACKS[BLADE_QUICK_ID] = cutFromCrescendo(BLADE_QUICK_ID, '速い連', BLADE_QUICK_STRIKES, BLADE_QUICK_GAPS_MS, QUICK, 12)
    ATTACKS[BLADE_GRAND_ID] = cutFromCrescendo(BLADE_GRAND_ID, '大振り', BLADE_GRAND_STRIKES, BLADE_GRAND_GAPS_MS, GRAND, 16)
    def.baseAttackIds.push(BLADE_QUICK_ID, BLADE_GRAND_ID)
    def.unlockByExecutes?.[0]?.ids.push(BLADE_GRAND_ID)
    def.unlockByExecutes?.[1]?.ids.push(BLADE_QUICK_ID)
    installed = true
  }
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_QUICK_ID]: BLADE_QUICK_TIMELINE, [BLADE_GRAND_ID]: BLADE_GRAND_TIMELINE }
  def.strings = waltzStrings(buildArleStrings())
  useBladeDancerWaltz([A, BETA, HEM, CRES, BLADE_GAMMA_ID, BLADE_PULL_ID, BLADE_DELTA_ID, BLADE_SPIN_ID, BLADE_THROW_ID, BLADE_QUICK_ID, BLADE_GRAND_ID])
  setBladeClapEnabled(true)
  // 崩れ・倒れ (と弾かれ) を立ち絵の使い回しから本描きへ (10-29・作者 c1「絵・技・数値・とどめまで本物」)。
  //   絵は 10-19 に描いた <key>_1019 (?bladeMoves=1 の比較版と同じ)。弾かれの絵は配布版の見せ方 (押し戻す) では出ない。
  for (const key of BLADE_DANCER_MOVE_STATE_KEYS) setSpriteFileOverride(BOSS_ID, '', key, `${key}_1019`)
  // 崩れの入り (最初の 0.25 秒) は、膝が折れて剣が指からすべり落ちかける絵 (10-29 Codex 描き) → 片膝をつく staggered_1019。
  for (const [key, stem] of BLADE_STAGGER_STEMS) setSpriteFileOverride(BOSS_ID, '', key, stem)
  // 幕引きの一閃 (とどめ) の溜めは、客席へ深く一礼する絵 (10-29 作者 b4「AI が剣舞の線で描いて決める」)。
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_EXEC_ID]: BLADE_EXEC_TIMELINE }
}

export const BLADE_EXEC_ID = 'dancerExecution'
export const BLADE_EXEC_BOW = 'm1029_exec_bow'
/** 幕引きの一閃で初めて出る絵 (11-04 に配布版で測った順)。戦いの頭の手すきに前もって測る = src/sprites.ts warmBossSpriteMeta。 */
export const BLADE_EXEC_WARM_KEYS: readonly string[] = [...CLAP_KEYS, 'glide', 'exec_slash', 'm1020_v6_e4']
export const BLADE_STAGGER_STEMS: readonly (readonly [SpriteKey, string])[] = [
  ['staggered_enter', 'm1029_defeat_fall'],
  ['staggered_hold', 'staggered_1019'],
]
/** 幕引きの一閃 = 一礼 → 沈む → 滑り込む → 交差して斬り抜ける → 両腕を広げた締め → ワルツの構え。 */
export const BLADE_EXEC_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0, image: BLADE_EXEC_BOW, hold: true, tag: 'anticipation', ease: 'easeIn' },
  { phase: 'telegraph', at: 0.7, image: BLADE_EXEC_BOW, hold: true, scale: { y: 0.92 }, ease: 'step' },
  { phase: 'telegraph', at: 0.85, image: 'glide', hold: true },
  { phase: 'active', at: 0, image: 'exec_slash', hold: true, tag: 'contact' },
  { phase: 'recovery', at: 0, image: 'exec_slash', hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.6, image: 'm1025_w_3', hold: true, tag: 'recover' },
  { phase: 'recovery', at: 0.85, image: 'idle', hold: true },
]

/** 外す (テストの後始末用)。 */
export function restoreBladeDancerArle(): void {
  if (!installed) return
  restoreBladeDancerWaltz()   // 技ごとの後始末より先に (ワルツは技の並びの上にかぶせている)
  setBladeClapEnabled(false)
  for (const key of BLADE_DANCER_MOVE_STATE_KEYS) setSpriteFileOverride(BOSS_ID, '', key, 'idle')
  for (const [key] of BLADE_STAGGER_STEMS) setSpriteFileOverride(BOSS_ID, '', key, null)
  restoreBladeDancerThrow()
  restoreBladeDancerSpin()
  restoreBladeDancerDelta()
  restoreBladeDancerPull()
  restoreBladeDancerGamma()
  restoreBladeDancerPirouette()
  delete ATTACKS[BLADE_QUICK_ID]
  delete ATTACKS[BLADE_GRAND_ID]
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = baseSnap
  def.unlockByExecutes = unlockSnap
  delete def.strings
  const tls = BOSS_ATTACK_TIMELINES[BOSS_ID]
  if (tls) { delete tls[BLADE_QUICK_ID]; delete tls[BLADE_GRAND_ID]; delete tls[BLADE_EXEC_ID] }
  installed = false
}

// 10-29 から配布版の既定 (作者 b1「アルレ版の連17本をそのまま土台にする」)。?bladeArle=0 で前の4技の一戦。
export function parseBladeArle(v: string | null): boolean {
  return v !== '0'
}
